import { useState, useRef, useEffect, useCallback } from 'react';

export default function useCanvas({
  backgroundImage, loadedSilhouette, silhouetteScale, silhouetteOpacity,
  silhouetteOffsetX, silhouetteOffsetY, dispatchTransform, fileInputRef, isCameraActive,
}) {
  const canvasRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [initialDragOffsets, setInitialDragOffsets] = useState({ x: 0, y: 0 });

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!backgroundImage) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      canvas.width = canvas.width || 500;
      canvas.height = canvas.height || 300;
      return;
    }
    const bgImg = new Image();
    bgImg.onload = () => {
      canvas.width = bgImg.naturalWidth;
      canvas.height = bgImg.naturalHeight;
      ctx.drawImage(bgImg, 0, 0);
      if (loadedSilhouette && loadedSilhouette.naturalWidth > 0) {
        const scaledWidth = loadedSilhouette.naturalWidth * silhouetteScale;
        const scaledHeight = loadedSilhouette.naturalHeight * silhouetteScale;
        const x = (canvas.width - scaledWidth) / 2 + silhouetteOffsetX;
        const y = silhouetteOffsetY;
        ctx.globalAlpha = silhouetteOpacity;
        ctx.drawImage(loadedSilhouette, x, y, scaledWidth, scaledHeight);
        ctx.globalAlpha = 1.0;
      }
      const watermarkText = "Nutscaper.com";
      const padding = 15;
      ctx.font = "bold 16px Arial";
      ctx.fillStyle = "rgba(255, 255, 255, 0.5)";
      ctx.textAlign = "right";
      ctx.textBaseline = "bottom";
      ctx.fillText(watermarkText, canvas.width - padding, canvas.height - padding);
    };
    bgImg.onerror = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    };
    bgImg.src = backgroundImage;
  }, [backgroundImage, loadedSilhouette, silhouetteScale, silhouetteOpacity, silhouetteOffsetX, silhouetteOffsetY]);

  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  const getEventPos = (canvas, e) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const isPointInSilhouette = (pointX, pointY) => {
    const canvas = canvasRef.current;
    if (!loadedSilhouette || !canvas || loadedSilhouette.naturalWidth === 0) return false;
    const scaledWidth = loadedSilhouette.naturalWidth * silhouetteScale;
    const scaledHeight = loadedSilhouette.naturalHeight * silhouetteScale;
    const silX = (canvas.width - scaledWidth) / 2 + silhouetteOffsetX;
    const silY = silhouetteOffsetY;
    return pointX >= silX && pointX <= silX + scaledWidth && pointY >= silY && pointY <= silY + scaledHeight;
  };

  const handleCanvasMouseDown = (event) => {
    if (!loadedSilhouette || !canvasRef.current) return;
    const pos = getEventPos(canvasRef.current, event);
    if (isPointInSilhouette(pos.x, pos.y)) {
      setIsDragging(true);
      setDragStartPos(pos);
      setInitialDragOffsets({ x: silhouetteOffsetX, y: silhouetteOffsetY });
      event.preventDefault();
    }
  };

  const handleCanvasMouseMove = (event) => {
    if (!isDragging || !loadedSilhouette) return;
    const pos = getEventPos(canvasRef.current, event);
    const deltaX = pos.x - dragStartPos.x;
    const deltaY = pos.y - dragStartPos.y;
    dispatchTransform({ type: 'SET_TRANSFORM', payload: { offsetX: initialDragOffsets.x + deltaX, offsetY: initialDragOffsets.y + deltaY } });
  };

  const handleCanvasMouseUp = () => setIsDragging(false);
  const handleCanvasMouseLeave = () => setIsDragging(false);

  const handleCanvasTouchStart = (e) => {
    e.preventDefault();
    if (!loadedSilhouette || !canvasRef.current) return;
    const pos = getEventPos(canvasRef.current, e);
    if (isPointInSilhouette(pos.x, pos.y)) {
      setIsDragging(true);
      setDragStartPos(pos);
      setInitialDragOffsets({ x: silhouetteOffsetX, y: silhouetteOffsetY });
    }
  };

  const handleCanvasTouchMove = (e) => {
    e.preventDefault();
    if (!isDragging || !loadedSilhouette) return;
    const pos = getEventPos(canvasRef.current, e);
    const deltaX = pos.x - dragStartPos.x;
    const deltaY = pos.y - dragStartPos.y;
    dispatchTransform({ type: 'SET_TRANSFORM', payload: { offsetX: initialDragOffsets.x + deltaX, offsetY: initialDragOffsets.y + deltaY } });
  };

  const handleCanvasTouchEnd = () => setIsDragging(false);

  const handleCanvasAreaClick = () => {
    if (!backgroundImage && !isCameraActive && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return {
    canvasRef, isDragging,
    handleCanvasMouseDown, handleCanvasMouseMove, handleCanvasMouseUp, handleCanvasMouseLeave,
    handleCanvasTouchStart, handleCanvasTouchMove, handleCanvasTouchEnd, handleCanvasAreaClick,
  };
}
